'use strict';
import template from './mx-hierarchy-picker.html';

/**
 * @ngdoc component
 * @name mx.components:mxHierarchyPicker
 * @module mx.components
 * @restrict 'E'
 * @scope {}
 * @description Hierarchy picker. Allows to search/select a node from tree structure.
 *
 * @param {string} label@ - Defines control label displayed on the form.
 * @param {boolean} required= - The required property sets or returns whether a hierarchy picker must be filled out before submitting a form.
 * @param {boolean} disabled= - The disabled property sets or returns whether a hierarchy picker should be disabled, or not.
 * @param {boolean} readOnly= - The readOnly property sets or returns whether the contents of a mxPicker should be read-only.

 * @param {object} ngModel=ngModel - directive binds element to the object in the model. Supposed to contain an `id` of selected tree-node.

 * @param {string} inputDataType= - can be "tree" or "list" (Default 'tree'). Read about `loadTree` for derails.

 * @param {function(searchText:string, ID): object} loadTree= - Callback function for loading
 * tree-structure by given `searchText` or/and `ID`, and `parentId` if it's need to search in sub-tree under the node with this `parentId`.
 * Returns a promise which will be resolved with tree-structure.
 * This `tree-structure`depends on `inputDataType` parameter. If `inputDataType`==="tree" (default) then control expects hierarchical format like this:
 * [
 *    {
 * 		id: 1,
 * 		title: 'AAA',
 * 		children: [{
 * 			id: 10,
 * 			title: 'A-111',
 * 			children: [{
 * 				id: 100,
 * 				title: 'A-1-ZZZ',
 * 				children: [...]
 * 			}]
 * 		}, {
 * 			id: 11,
 * 			title: 'A-222'
 * 		},
 * 		...
 * 		]
 * 	}, {
 * 		id: 2,
 * 		title: 'BBB',
 * 		children: [...]
 * 	}, {
 * 		id: 3,
 * 		title: 'CCC',
 * 		children: [...]
 * 	},
 *    ....
 * ]
 * otherwise, if `inputDataType`==="list" then control expects plain format like this
 * (this format can be more usable for Server-Side with SQL DB):
 * [
 *    {id: 1, title: 'AAA'},
 *    {id: 10, title: 'A-111', __parents: [{id: 1, title: 'AAA'}]},                // actually `parents` array can contain just links to same objects (like JS works)
 *    {id: 11, title: 'A-222', __parents: [{id: 1, title: 'AAA'}]},
 *    ... ,
 *    {id: 100, title: 'A-1-ZZZ', __parents: [{id: 1, title: 'AAA'}, {id: 11, ...}]},
 *    {id: 101, title: 'A-1-YYY', __parents: [{id: 1, title: 'AAA'}, {id: 11, ...}]},
 *    {id: 2, title: 'BBB'},
 *    {id: 20, title: 'BBB-222', __parents: [...]},
 *    ....
 *    {id: 3, title: 'CCC'},
 * ]
 *
 *
 * @usage
 *    <mx-hierarchy-picker
 *        label="Hierarchy Picker"
 *        ng-model="vm.hierarchyModel"
 *        load-tree="vm.loadTreeCallback">
 *    </mx-hierarchy-picker>
 *
 *
 * @usage
 *    <mx-hierarchy-picker
 *        label="Hierarchy Picker"
 *        ng-model="vm.hierarchyModel"
 *        input-data-type="list"
 *        load-tree="vm.loadTreeAsListCallback">
 *    </mx-hierarchy-picker>
 *
 */

angular.module('mx.components').directive('mxHierarchyPicker', function mxHierarchyPickerDirective() {
	const directive = new mx.components.FormControlBase(MxHierarchyPickerCtrl, null, template);
	angular.extend(directive.bindToController, {
		loadTree: '<',
		navigateItem: '=?',
		browseLookup: '=?',
		ngRequired: '=?',
		required: '=?require',
		inputDataType: '@?',
		notFound: '=?',
		loadDelay: '@',
	});
	delete directive.link;
	return directive;
});

MxHierarchyPickerCtrl.$inject = ['$scope', '$q', '$attrs', 'mx.shell.InternationalizationService'];

function MxHierarchyPickerCtrl($scope, $q, $attrs, i18nService) {
	var vm = this;
	const arrowSymbols = i18nService.currentLanguage.isRightToLeft ? [' ◄ ', ' < '] : [' ► ', ' > '];

	vm.$onInit = function () {
		vm.dropdownLabelsDecorator = dropdownLabelsDecorator;
		vm.inputDataType = vm.inputDataType ? vm.inputDataType.toLowerCase() : 'tree';
		vm.loadDelay = vm.loadDelay || 1000;

		$scope.$watch('vm.model', function () {
			var m = vm.model;
			var c = vm.idsChain;
			var id = Array.isArray(m) ? m[m.length - 1] : m;

			if (id && id !== (Array.isArray(c) && c[c.length - 1])) {
				loadItemsDynamically(undefined, id).then(function (nodesData) {
					var selectedNode = nodesData.filter(function (n) {
						return n.id === id;
					})[0];
					if (selectedNode) {
						vm.selectedItemChain = (selectedNode.__parents || []).concat([selectedNode]);
						vm.idsChain = vm.selectedItemChain.map(function (n) {
							return n.id;
						});
						_debouncedOnChange();
					}
				});
			} else if (!id) {
				vm.idsChain = Array.isArray(vm.idsChain) ? [] : vm.idsChain;
				vm.selectedItemChain = [];
				_debouncedOnChange();
			}
		});

		$scope.$watch('vm.idsChain', function () {
			var m = vm.model;
			var c = vm.idsChain;
			if (m !== c && m !== (Array.isArray(c) && c[c.length - 1])) {
				vm.model = Array.isArray(c) ? c[c.length - 1] : c;
				_debouncedOnChange();
			}
		});

		var _debouncedOnChange = $.debounce(50, function () {
			$scope.$evalAsync(function () {
				vm.onChange({
					data: vm.model,
					onChangeData: { selectedItemChain: vm.selectedItemChain },
				});
			});
		});

		if ($attrs.browseLookup) {
			vm.hierarchyBrowseLookup = function () {
				var res = $q.when(null),
					selectedId;
				if (vm.browseLookup) {
					selectedId = (vm.idsChain && vm.idsChain.length && vm.idsChain[vm.idsChain.length - 1]) || null;
					res = vm.browseLookup(selectedId).then(function (res) {
						return loadItemsDynamically(undefined, res.id).then(function (nodesData) {
							var selectedNode = nodesData.filter(function (n) {
								return n.id === res.id;
							})[0];
							if (selectedNode) {
								return (selectedNode.__parents || []).concat([selectedNode]);
							}
							return $q.reject();
						});
					});
				}
				return res;
			};
		}
	};

	vm.loadItems = function (searchText) {
		searchText = _prepareSearchText(searchText);
		return loadItemsDynamically(searchText, undefined, vm.idsChain && vm.idsChain[vm.idsChain.length - 1]);
	};

	vm.onAdd = function (node) {
		vm.selectedItemChain = (node.__parents || []).slice();
		vm.selectedItemChain.push(node);
		vm.idsChain = vm.selectedItemChain.map(function (el) {
			return el.id;
		});
	};

	vm.onRemove = function (node) {
		var pos = vm.idsChain.indexOf(node.id);
		vm.idsChain = vm.idsChain.slice(0, pos);
		vm.selectedItemChain = vm.selectedItemChain.filter(function (n, i) {
			return vm.idsChain[i] === n.id;
		});
	};

	function loadItemsDynamically(searchText, id, parentId) {
		return vm.loadTree
			? vm.loadTree(searchText, id, parentId).then(function (nodesData) {
					return (vm.inputDataType === 'tree' ? unwrapListFromTree(nodesData) : prepareList(nodesData)).filter(function (leaf) {
						return doesLabelMatchTheQuery(leaf.__nodeLabel, searchText);
					});
			  })
			: $q.when([]);
	}

	function unwrapListFromTree(tree, parents) {
		var res = [];
		var lastParent = parents && parents.length ? parents[parents.length - 1] : undefined;

		if (tree) {
			tree = Array.isArray(tree) ? tree : [tree];
		} else {
			tree = [];
		}

		tree.forEach(function (node) {
			node.__nodeLabel = (lastParent ? lastParent.__nodeLabel + arrowSymbols[1] : '') + node.title;
			node.__title = (parents && parents.length ? arrowSymbols[0] : '') + node.title;
			if (parents) {
				node.__parents = parents;
			}
			res.push(node);
			if (node.children && node.children.length) {
				var newParents = [].concat(parents || []);
				newParents.push(node);
				res = res.concat(unwrapListFromTree(node.children, newParents));
			}
		});
		return res;
	}

	function prepareList(treeList) {
		return treeList.map(function (node) {
			node.__nodeLabel =
				(node.__parents || [])
					.map(function (n) {
						return n.title + arrowSymbols[1];
					})
					.join('') + node.title;
			node.__title = (node.__parents && node.__parents.length ? arrowSymbols[0] : '') + node.title;
			(node.__parents || []).forEach(function (pNode, i) {
				pNode.__title = (i > 0 ? arrowSymbols[0] : '') + pNode.title;
			});

			return node;
		});
	}

	function doesLabelMatchTheQuery(label, searchText) {
		var res = false,
			ignorePrefix = (vm.selectedItemChain || [])
				.map(function (n) {
					return n.title;
				})
				.join(arrowSymbols[1]),
			labelLow,
			searchTextLow,
			searchTextParts,
			searchTextPosArr = [],
			i,
			pos = 0;

		if (!searchText || label.indexOf(ignorePrefix) === 0) {
			label = label.substring(ignorePrefix.length);
			labelLow = (label || '').toLowerCase();
			searchTextLow = (searchText || '').toLowerCase();
			searchTextParts = searchTextLow
				.split(/([\s+()[\]{}])/)
				.map(function (s) {
					return s.trim();
				})
				.filter(function (s) {
					return s;
				});

			for (i = 0; i < searchTextParts.length; i++) {
				pos = labelLow.indexOf(searchTextParts[i], pos + (i > 0 ? searchTextParts[i - 1].length : 0));
				if (pos !== -1) {
					searchTextPosArr.push(pos + ignorePrefix.length);
				} else {
					break;
				}
			}
			res = searchTextPosArr.length === searchTextParts.length && {
				searchTextPosArr: searchTextPosArr,
				searchTextParts: searchTextParts,
				ignorePrefix: ignorePrefix,
			};
		}

		return res;
	}

	function dropdownLabelsDecorator(label, searchText) {
		var i,
			pos = -1,
			endPos = -1,
			matchRes;

		searchText = _prepareSearchText(searchText);

		matchRes = doesLabelMatchTheQuery(label, searchText);
		if (matchRes) {
			for (i = matchRes.searchTextPosArr.length - 1; i !== -1; i--) {
				pos = matchRes.searchTextPosArr[i];
				endPos = pos + matchRes.searchTextParts[i].length;
				label = label.substring(0, pos) + '<span class="mx-multipicker-dropdown-highlight">' + label.substring(pos, endPos) + '</span>' + label.substring(endPos);
			}
		}

		return label;
	}

	function _prepareSearchText(text) {
		// "%" is processing like a space " ".
		return text && text.split('%').join(' ').trim();
	}
}
